
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { PERMISSION } from '@/permissions';
import { toErrorMessage } from '@/utils/errorHandling';
import Autocomplete from '@/components/shared/Autocomplete.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import Toast from '@/utils/toast';

const namespace: string = 'enterpriseMenuPull';

@Component<EnterpriseMenuPullStartForm>({
	components: {
		Autocomplete,
		InfoBanner,
		TextButton,
		ToggleSwitch,
		WarningModal
	}
})
export default class EnterpriseMenuPullStartForm extends Vue {
	@Prop({ type: Array, required: false, default: () => [] }) private initialLocationIds!: string[];
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getRestaurants', { namespace: 'auth' }) private allLocations!: Tenant[] | null;
	@Getter('getAuthGroups', { namespace: 'auth' }) private authGroups!: string[] | null;
	@Getter('hasActiveJob', { namespace }) private hasActiveJob!: boolean;
	@Action('startJob', { namespace }) private startJob!: (payload: StartMenuPullPayload) => Promise<void>;

	// Feature flag: flip to true to re-enable the overwrite toggle + confirmation modal.
	// Deferred pending product/backend readiness.
	private readonly overwriteEnabled: boolean = false;
	private overwrite: boolean = false;
	private showOverwriteConfirm: boolean = false;
	private selectedGroups: string[] = [];
	private selectedLocationIds: string[] = [];
	private isStarting: boolean = false;
	private PERMISSION = PERMISSION;

	private get posIntegratedLocations(): Tenant[] {
		return (this.allLocations || []).filter((r: Tenant) => r.pos_integrated);
	}

	/**
	 * Auth0 groups the user has access to, narrowed to those with at least one POS-integrated
	 * location in the user's location list. A group with no integrated locations has nothing to
	 * pull, so we don't surface it in the dropdown.
	 *
	 * @return {string[]}
	 */
	private get availableGroups(): string[] {
		const groups: string[] = this.authGroups || [];
		const integratedGroups: Set<string> = new Set();
		(this.allLocations || []).forEach((r: Tenant) => {
			if (!r.pos_integrated) {
				return;
			}
			if (r.group) {
				integratedGroups.add(r.group);
			}
			if (r.groups) {
				r.groups.forEach((g: string) => g && integratedGroups.add(g));
			}
		});
		return groups.filter((g: string) => integratedGroups.has(g));
	}

	private get locationItems(): (Tenant & { displayName: string })[] {
		return this.posIntegratedLocations.map((r: Tenant) => ({
			...r,
			displayName: r.external_identifier ? `${r.name} (${r.external_identifier})` : r.name
		}));
	}

	private get allLocationsSelected(): boolean {
		return this.posIntegratedLocations.length > 0
			&& this.selectedLocationIds.length === this.posIntegratedLocations.length;
	}

	private get hasSelection(): boolean {
		return this.selectedGroups.length > 0 || this.selectedLocationIds.length > 0;
	}

	private get canStart(): boolean {
		return !this.hasActiveJob && !this.isStarting && this.hasSelection;
	}

	/**
	 * Pre-populate from the retry-failed flow (parent passes the failed IDs as a prop).
	 *
	 * @return {void}
	 */
	private created(): void {
		if (this.initialLocationIds.length) {
			this.selectedLocationIds = [...this.initialLocationIds];
		}
	}

	private identity(value: string): string {
		return value;
	}

	private onGroupsChange(groups: string[]): void {
		this.selectedGroups = groups;
		if (groups.length) {
			this.selectedLocationIds = [];
		}
	}

	private onLocationsInput(ids: string[]): void {
		this.selectedLocationIds = ids;
		if (ids.length) {
			this.selectedGroups = [];
		}
	}

	private selectAllLocations(): void {
		this.onLocationsInput(this.posIntegratedLocations.map((r: Tenant) => r.id));
	}

	/**
	 * Custom autocomplete filter: matches a location by name or external identifier (center number).
	 * Supports a comma-separated list of terms with OR semantics, so a user can paste multiple
	 * names/center numbers and surface every matching location at once.
	 *
	 * @param {Tenant} item
	 * @param {string} queryText
	 * @return {boolean}
	 */
	private locationFilter(item: Tenant, queryText: string): boolean {
		const terms: string[] = queryText.split(',').map((t: string) => t.trim().toLowerCase()).filter(Boolean);
		if (!terms.length) {
			return true;
		}
		const name: string = (item.name || '').toLowerCase();
		const ext: string = (item.external_identifier || '').toLowerCase();
		return terms.some((term: string) => name.includes(term) || ext.includes(term));
	}

	private onOverwriteToggle(value: boolean): void {
		this.overwrite = value;
		if (value) {
			this.showOverwriteConfirm = true;
		}
	}

	private cancelOverwriteConfirm(): void {
		this.showOverwriteConfirm = false;
		this.overwrite = false;
	}

	private confirmOverwrite(): void {
		this.showOverwriteConfirm = false;
	}

	private async start(): Promise<void> {
		if (!this.canStart) {
			return;
		}
		this.isStarting = true;
		try {
			await this.startJob(this.selectedLocationIds.length
				? { overwrite: this.overwrite, restaurantIds: this.selectedLocationIds }
				: { overwrite: this.overwrite, groups: this.selectedGroups }
			);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isStarting = false;
		}
	}
}
